import React, { createContext, useContext, useRef, useState } from "react"
import { API, Auth } from "aws-amplify"
// import awsconfig from "../aws-exports"
import { OrganizationContext } from "./org-context"
import { CognitoUserInterface } from "@aws-amplify/ui-components"
import { loadStripe } from "@stripe/stripe-js"
import moment from "moment"
import TagManager from "react-gtm-module"
import { sha256 } from "js-sha256"

// Amplify.configure(awsconfig)
// Amplify.configure({
//   aws_cloud_logic_custom: [
//     {
//       name: "azingorest",
//       endpoint:
//         "https://51r9h0kjmh.execute-api.us-east-1.amazonaws.com/staging",
//       region: "us-east-1",
//     },
//   ],
// })

export interface CheckoutRequest {
  priceId: string
  quantity: number
  mode: string
  clientReferenceId?: string
  automaticTax?: boolean
  stripeCustomerId?: string
  successPath?: string
  cancelPath?: string
}

interface StripeState {
  onboardingURL: string
  stripeAccountId: string
  onboardingComplete: boolean
  checkoutSessionResults: any
  createStripeAccount: (action: string) => Promise<void>
  generateAccountLink: () => Promise<void>
  getOnboardingStatus: () => Promise<void>
  getCheckoutSessionResults: (sessionID: string) => Promise<void>
  redirectToCheckout: (checkoutParams: CheckoutRequest) => Promise<void>
}

export const StripeContext = createContext<StripeState>({
  onboardingURL: "",
  stripeAccountId: "",
  onboardingComplete: false,
  checkoutSessionResults: {},
  createStripeAccount: async () => {},
  generateAccountLink: async () => {},
  getOnboardingStatus: async () => {},
  getCheckoutSessionResults: async () => {},
  redirectToCheckout: async () => {},
})

interface StripeContextProps {
  children: React.ReactNode
}

const StripeContextProvider = ({ children }: StripeContextProps) => {
  const debug = true
  const [onboardingURL, setOnboardingURL] = useState<string>("")
  const [onboardingComplete, setOnboardingComplete] = useState<boolean>(false)
  const [checkoutSessionResults, setCheckoutSessionResults] = useState<any>()
  const { updateOrganization, currentOrganization } =
    useContext(OrganizationContext)
  const [stripeAccountId, setStripeAccountId] = useState<string>("")
  const latestAccountID = useRef("")

  let spk: string = `${process.env.GATSBY_STRIPE_PUBLISHABLE_KEY}`!

  function getOrgHandle(user: CognitoUserInterface | null) {
    if (debug) {
      // console.log("calling getOrgHandle")
    }
    let orgHandle = user ? user?.attributes["custom:organization"] : null
    orgHandle = orgHandle?.replace(/\W+(?!$)/g, "-")?.toLowerCase()
    orgHandle = orgHandle?.replace(/\W$/, "")?.toLowerCase()
    // console.log("STRIPECONTEXT: orgHandle", orgHandle)
    return orgHandle
  }

  async function getSignedInUser(): Promise<CognitoUserInterface | null> {
    if (debug) {
      // console.log("calling getSignedInUser")
    }
    try {
      return await Auth.currentAuthenticatedUser()
    } catch {
      return null
    }
  }

  async function getCurrentOrg(handle: string, token: string): Promise<any> {
    if (currentOrganization) return currentOrganization
    if (debug) {
      // console.log("calling getCurrentOrg")
    }
    const requestPath = "/org?handle=" + handle
    const requestInfo = {
      headers: { Authorization: token },
    }
    // console.log("requestInfo", requestInfo)
    let returnOrg
    try {
      if (handle) {
        returnOrg = await API.get("azingorest", requestPath, requestInfo)
        if (debug) {
          // console.log("STRIPECONTEXT: currentOrgResults", returnOrg)
        }
        return returnOrg.body
      }
    } catch (error) {
      // console.log("STRIPECONTEXT: Error", error)
      returnOrg = null
    }
  }

  const createStripeAccount = async (action: string) => {
    if (debug) {
      // console.log("calling createStripeAccount")
    }
    let webInfo: any

    try {
      const signedInUser: CognitoUserInterface | null = await getSignedInUser()
      const authToken = signedInUser?.signInUserSession?.idToken?.jwtToken
      const handle = getOrgHandle(signedInUser)
      const currentOrg = await getCurrentOrg(handle, authToken)
      if (debug) {
        // console.log("STRIPECONTEXT: createStripeAccount currentOrg", currentOrg)
      }
      webInfo = await retrieveCurrentWebsiteData()
      if (debug) {
        // console.log("STRIPECONTEXT: webInfo", webInfo)
      }
      const requestInfo = {
        headers: {
          Authorization: authToken,
          "Content-Type": "application/json",
        },
        body: {
          action: action,
          attributes: signedInUser?.attributes,
          companyName: currentOrg?.name,
          productDescription: webInfo?.content?.businessDescription,
          supportPhone: webInfo?.content?.contactPhone,
          supportEmail: webInfo?.content.contactEmail,
        },
      }
      if (debug) {
        // console.log("requestInfo", requestInfo)
      }

      if (
        currentOrg?.paymentConfig?.stripeAccountID ||
        stripeAccountId ||
        latestAccountID.current
      ) {
        if (debug) {
          // console.log("STRIPECONTEXT: account exists")
        }
        generateAccountLink()
        latestAccountID.current = latestAccountID.current
          ? latestAccountID.current
          : currentOrg?.paymentConfig?.stripeAccountID || stripeAccountId
      } else {
        if (debug) {
          // console.log("STRIPECONTEXT: account doesnt exist")
        }
        const resp = await API.put("azingorest", "/stripe", requestInfo)
        // console.log("STRIPECONTEXT: create stripe account", resp)
        let newOrgObject = currentOrg
        newOrgObject.paymentConfig = newOrgObject.paymentConfig || {}
        newOrgObject.paymentConfig.stripeAccountID = resp?.body?.accountId
        if (debug) {
          // console.log(
          // "STRIPECONTEXT: newOrg in create Stripe Account ",
          // newOrgObject
          // )
        }
        await updateOrganization(newOrgObject)
        if (debug) {
          // console.log("STRIPECONTEXT: called UpdateOrganization")
        }
        setOnboardingURL(resp?.body?.url)
        // console.log(
        //   "STRIPECONTEXT:  new accountId response",
        //   resp?.body?.accountId
        // )
        setStripeAccountId(resp?.body?.accountId)
        latestAccountID.current = resp?.body?.accountId
      }
    } catch (err) {
      console.log("create account error", err)
      throw err
    }
  }

  const generateAccountLink = async () => {
    if (debug) {
      // console.log("calling generateAccountLink")
    }

    try {
      const signedInUser: CognitoUserInterface | null = await getSignedInUser()
      const authToken = signedInUser?.signInUserSession?.idToken?.jwtToken
      const handle = getOrgHandle(signedInUser)
      const currentOrg = await getCurrentOrg(handle, authToken)
      // console.log("STRIPECONTEXT: generateAccountLink currentOrg", currentOrg)
      const accountId =
        currentOrg?.paymentConfig?.stripeAccountID ||
        stripeAccountId ||
        latestAccountID.current
      const apiPath = "/stripe"
      const requestInfo = {
        headers: {
          Authorization: authToken,
        },
        body: {
          action: "get-account-link",
          accountId: accountId,
        },
      }
      if (!accountId) {
        // console.log("Missing account reference")
        throw "Missing account reference"
      }
      // console.log("accountId", accountId)
      const res = await API.post("azingorest", apiPath, requestInfo)
      if (res.body.onboardingLink) {
        setOnboardingURL(res.body.onboardingLink)
      } else {
        // console.log("Getting onboarding link", res)
      }
    } catch (err) {
      throw err
    }
  }

  const getOnboardingStatus = async () => {
    if (debug) {
      // console.log("calling getOnboardingStatus")
    }
    let handle: string = ""

    try {
      const possibleToken = (await Auth.currentSession())
        .getIdToken()
        .getJwtToken()
      const signedInUser = await getSignedInUser()
      handle = getOrgHandle(signedInUser)
      if (debug) {
        // console.log("STRIPECONTEXT: handle during status", handle)
      }
      const currentOrg = await getCurrentOrg(handle, possibleToken)
      if (debug) {
        // console.log("STRIPECONTEXT: currentOrg during status", currentOrg)
      }
      const accountID = currentOrg?.paymentConfig?.stripeAccountID
      const requestPath = "/stripe"
      const requestInfo = {
        headers: { Authorization: possibleToken },
        body: {
          action: "get-account-status",
          accountId: accountID,
        },
      }
      if (debug) {
        // console.log("requestInfo", requestInfo)
      }
      if (accountID) {
        const res = await API.post("azingorest", requestPath, requestInfo)
        // console.log("account status response:", res)
        setOnboardingComplete(res.body.charges_enabled)
        let newOrgObject = currentOrg
        // console.log("STRIPECONTEXT updatedOrgObject Pre", newOrgObject)
        newOrgObject.onboarding = newOrgObject.onboarding || {}
        // console.log("STRIPECONTEXT updatedOrgObject Post", newOrgObject)
        const currentProgress = newOrgObject?.onboarding?.paymentProgress
        if (res.body.charges_enabled) {
          newOrgObject.onboarding.paymentProgress = 100
        } else {
          let tasksRemaining = res.body.requirements.eventually_due.length
          tasksRemaining = Math.min(tasksRemaining, 7)
          const percentageComplete = Math.round(
            ((8 - tasksRemaining) / 8) * 100
          )
          newOrgObject.onboarding.paymentProgress = percentageComplete
        }
        if (currentProgress != newOrgObject.onboarding.paymentProgress) {
          await updateOrganization(newOrgObject)
        }
      }
    } catch (err) {
      // console.log("waiting for onboarding status", err)
    }
  }

  function getCookie(key: string) {
    var b = document.cookie.match("(^|;)\\s*" + key + "\\s*=\\s*([^;]+)")
    return b ? b.pop() : ""
  }

  const redirectToCheckout = async (checkoutRequest: CheckoutRequest) => {
    let handle: string = ""
    try {
      const possibleToken = (await Auth.currentSession())
        .getIdToken()
        .getJwtToken()
      // const possibleAttributes = (await Auth.currentUserInfo())?.attributes
      // const name = possibleAttributes?.name || possibleAttributes?.email
      // const userId = possibleAttributes?.sub
      // const stripeCustomerId = userId
      const signedInUser = await getSignedInUser()
      handle = getOrgHandle(signedInUser)
      if (debug) {
        // console.log("STRIPECONTEXT: handle during status", handle)
      }

      const tid = getCookie("_fprom_tid")
      const fpObject = {
        fp_tid: tid || "fp_" + Date.now(),
      }
      console.log("tid", tid)
      const currentOrg = await getCurrentOrg(handle, possibleToken)
      const action = "fetch-direct-session"
      const requestInfo = {
        headers: {
          Authorization: possibleToken,
          "Content-Type": "application/json",
        },
        body: {
          action: action,
          priceId: checkoutRequest.priceId,
          quantity: checkoutRequest.quantity,
          mode: checkoutRequest.mode,
          clientReferenceId: currentOrg?.id || currentOrganization?.id,
          // stripeCustomerId:
          //   checkoutRequest.stripeCustomerId || stripeCustomerId,
          automaticTax: true,
          successPath: checkoutRequest.successPath || `dashboard`,
          cancelPath: checkoutRequest.cancelPath || `plans`,
          metadata: tid ? fpObject : undefined,
          referrer_coupon: tid ? true : false,
        },
      }

      // console.log("stripe RequestInfo", requestInfo)

      const resp = await API.put("azingorest", "/stripe", requestInfo)
      const session = resp?.body?.session

      const sessionId = session?.id

      const checkoutStripePromise = loadStripe(spk)
      const stripe = await checkoutStripePromise
      stripe?.redirectToCheckout({ sessionId: sessionId })
    } catch (err) {
      // console.log("STRIPECONTEXT:: Anonymous purchase attempted", err)
      throw err
    }
  }

  const getCheckoutSessionResults = async (sessionID: string) => {
    if (debug) {
      // console.log("calling getCheckoutSessionResults")
    }

    try {
      const signedInUser: CognitoUserInterface | null = await getSignedInUser()
      const authToken = signedInUser?.signInUserSession?.idToken?.jwtToken
      const handle = getOrgHandle(signedInUser)
      const currentOrg = await getCurrentOrg(handle, authToken)
      // console.log("getCheckoutSessionResults:", currentOrg)
      const requestPath = "/stripe"
      const requestInfo = {
        headers: { Authorization: authToken },
        body: {
          action: "get-session-status",
          sessionId: sessionID,
          azingoUserId: signedInUser?.attributes?.sub,
        },
      }
      // console.log("SessionResults requestInfo", requestInfo)
      const res = await API.post("azingorest", requestPath, requestInfo)
      if (debug) {
        console.log("checkout session response:", res.body)
      }
      setCheckoutSessionResults(res.body)
      await setPlanProgress(res.body, currentOrg)
      if (typeof window !== "undefined") {
        let profile: any = {
          First_name: signedInUser?.attributes?.given_name,
          Last_name: signedInUser?.attributes?.family_name,
          Phone_number: signedInUser?.attributes?.phone_number,
          Hash_phone_number: sha256(signedInUser?.attributes?.phone_number),
          Email: signedInUser?.attributes?.email,
          Hash_email: sha256(signedInUser?.attributes?.email),
        }
        console.log("transaction profile", profile)
        let transaction: any = {
          id: res?.body?.session?.id,
          currency: res?.body?.session?.currency,
          amount: res?.body?.session?.amount_total
            ? (parseInt(res?.body?.session?.amount_total) / 100).toFixed(2)
            : 0,
          product_name: "Starter plan",
        }
        console.log("transaction details", transaction)
        TagManager.dataLayer({
          dataLayer: {
            event: "purchase",
            pagePath: "/plans",
            pageTitle: "Successful Purchase",
            profile: profile,
            transaction: transaction,
          },
        })
      }
    } catch (err) {
      // console.log("waiting checkout session to close", err)
      setCheckoutSessionResults(undefined)
    }
  }

  const setPlanProgress = async (sessionResults: any, currentOrg: any) => {
    if (debug) {
      // console.log("STRIPECONTEXT: calling setPlanProgress", sessionResults)
    }
    console.log("STRIPECONTEXT: current org setPlan:", currentOrg)
    if (sessionResults?.session?.payment_status === "paid") {
      let updatedOrgObject = currentOrg
      if (updatedOrgObject) {
        updatedOrgObject.onboarding = updatedOrgObject.onboarding || {}
        updatedOrgObject.onboarding.planProgress = 100
        const entitlementObject = {
          standard: {
            billingIssueDetectedAt: null,
            billingIssueDetectedAtMillis: null,
            expirationDate: moment().add(12, "months").toISOString(),
            identifier: "standard",
            isActive: true,
            isSandbox: false,
            latestPurchaseDate: moment().toISOString(),
            originalPurchaseDate: moment().toISOString(),
            ownershipType: "UNKNOWN",
            periodType: "NORMAL",
            productIdentifier: "prod_N1XMg68n9c8hre", //get from Session
            store: "WEB_STORE",
            unsubscribeDetectedAt: null,
            unsubscribeDetectedAtMillis: null,
            willRenew: true,
          },
        }
        updatedOrgObject.entitlements = entitlementObject
        console.log("STRIPECONTEXT: updating org setPlan:", updatedOrgObject)
        await updateOrganization(updatedOrgObject)
      }
    }
  }

  const retrieveCurrentWebsiteData = async () => {
    let tempWebObj = {}
    const signedInUser: CognitoUserInterface | null = await getSignedInUser()
    const authToken = signedInUser?.signInUserSession?.idToken?.jwtToken
    const useOrgHandle = getOrgHandle(signedInUser)
    const requestPath = "/website?orgHandle=" + useOrgHandle
    const requestParams = {
      headers: {
        Authorization: authToken,
      },
    }
    try {
      await API.get("azingorest", requestPath, requestParams).then(webObj => {
        if (webObj?.body) {
          tempWebObj = {
            id: webObj.body?.id,
            organizationID: webObj.body?.organizationID,
            url: webObj.body?.url,
            content: {
              heroSectionTitle: webObj.body?.content?.heroSectionTitle,
              heroSectionContent: webObj.body?.content?.heroSectionContent,
              aboutSectionContent: webObj.body?.content?.aboutSectionContent,
              businessDescription: webObj.body?.content?.businessDescription,
              contactPhone: webObj.body?.content?.contactPhone,
              contactEmail: webObj.body?.content?.contactEmail,
              social: {
                facebook: webObj.body?.content?.social?.facebook,
                twitter: webObj.body?.content?.social?.twitter,
                instagram: webObj.body?.content?.social?.instagram,
              },
            },
          }
          // console.log("Website response: ", tempWebObj)
          return tempWebObj
        }
      })
    } catch (error) {
      // console.log("Unable to load store: Error", error)
    }
    // console.log("no webObj Returned")
    return tempWebObj
  }

  return (
    <StripeContext.Provider
      value={{
        onboardingURL,
        stripeAccountId,
        onboardingComplete,
        generateAccountLink,
        createStripeAccount,
        getOnboardingStatus,
        getCheckoutSessionResults,
        checkoutSessionResults,
        redirectToCheckout,
      }}
    >
      {children}
    </StripeContext.Provider>
  )
}

export default StripeContextProvider
